"use client";

/**
 * @contract
 *   Назначение: Клиентский поиск/фильтр каталога учебных центров (DB-backed).
 *   Маршрут:   /tools/uchebne-centry
 *   Вход:      props.schools — строки public.schools (сервер, уже из БД).
 */

import { useMemo, useState } from "react";
import { Search, MapPin, Calendar, AlertTriangle, Building2, ExternalLink, MessageSquarePlus } from "lucide-react";
import {
  type SchoolCard,
  getRegions,
  searchSchools,
  isLicenseDanger,
} from "./skools-data";
import { SchoolReviews } from "./school-reviews";

export function SkoolSearch({ schools }: { schools: SchoolCard[] }) {
  const [query, setQuery] = useState("");
  const [selectedRegion, setSelectedRegion] = useState("");
  const [showExpired, setShowExpired] = useState(false);

  const regions = useMemo(() => getRegions(schools), [schools]);

  const filtered = useMemo(() => {
    let result = searchSchools(schools, query);
    if (selectedRegion) result = result.filter((s) => s.region === selectedRegion);
    if (!showExpired) {
      result = result.filter((s) => {
        if (!s.license_to) return true; // бессрочная — ок
        const ts = Date.parse(s.license_to);
        return Number.isNaN(ts) ? true : ts >= Date.now();
      });
    }
    return result;
  }, [schools, query, selectedRegion, showExpired]);

  return (
    <div>
      <div className="flex flex-col sm:flex-row gap-3 mb-8">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-[hsl(var(--portal-text-dim))]" strokeWidth={1.5} />
          <input
            type="text"
            placeholder="Поиск по названию, городу, региону или ИНН..."
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            className="w-full pl-10 pr-4 py-3 border border-[hsl(var(--portal-border))] bg-[hsl(var(--portal-surface))] text-[hsl(var(--portal-text))] placeholder:text-[hsl(var(--portal-text-dim))] font-sans text-sm focus:outline-none focus:border-[hsl(var(--portal-accent))] transition-colors"
          />
        </div>
        <select
          value={selectedRegion}
          onChange={(e) => setSelectedRegion(e.target.value)}
          className="px-4 py-3 border border-[hsl(var(--portal-border))] bg-[hsl(var(--portal-surface))] text-[hsl(var(--portal-text))] font-sans text-sm focus:outline-none focus:border-[hsl(var(--portal-accent))] transition-colors min-w-[200px]"
        >
          <option value="">Все регионы ({regions.length})</option>
          {regions.map((r) => (
            <option key={r.name} value={r.name}>
              {r.name} ({r.count})
            </option>
          ))}
        </select>
      </div>

      <div className="flex items-center gap-4 mb-6 text-sm flex-wrap">
        <label className="flex items-center gap-2 cursor-pointer text-[hsl(var(--portal-text-secondary))] font-sans">
          <input
            type="checkbox"
            checked={showExpired}
            onChange={(e) => setShowExpired(e.target.checked)}
            className="w-4 h-4 border border-[hsl(var(--portal-border-hover))] bg-[hsl(var(--portal-surface))] accent-[hsl(var(--portal-accent))]"
          />
          Показывать с истекшей лицензией
        </label>
        <span className="text-[10px] uppercase tracking-widest text-[hsl(var(--portal-text-muted))]">
          Найдено: <span className="font-mono tabular-nums text-[hsl(var(--portal-text-secondary))]">{filtered.length}</span> из{" "}
          <span className="font-mono tabular-nums text-[hsl(var(--portal-text-secondary))]">{schools.length}</span>
        </span>
      </div>

      {filtered.length === 0 ? (
        <div className="text-center py-16">
          <Search className="w-12 h-12 mx-auto mb-4 text-[hsl(var(--portal-text-dim))]" strokeWidth={1.5} />
          <p className="text-lg text-[hsl(var(--portal-text-secondary))] font-sans">Ничего не найдено</p>
          <p className="text-sm mt-1 text-[hsl(var(--portal-text-muted))] font-sans">Попробуйте изменить параметры поиска</p>
        </div>
      ) : (
        <div className="grid gap-3">
          {filtered.map((skool) => (
            <SkoolCard key={skool.inn} skool={skool} />
          ))}
        </div>
      )}
    </div>
  );
}

function SkoolCard({ skool }: { skool: SchoolCard }) {
  const [showReviews, setShowReviews] = useState(false);
  const licDanger = isLicenseDanger(skool);
  const isl = `https://islod.obrnadzor.gov.ru/rlic/?eoName=${encodeURIComponent(skool.inn)}`;
  const islStatus = (skool.license_status ?? "").toLowerCase();

  return (
    <div
      className={`border p-4 md:p-5 transition-colors ${
        licDanger
          ? "border-[hsl(var(--portal-danger))]/40 bg-[hsl(var(--portal-danger))]/10"
          : "border-[hsl(var(--portal-border))] bg-[hsl(var(--portal-surface))] hover:border-[hsl(var(--portal-border-hover))]"
      }`}
    >
      <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-3">
        <div className="flex-1 min-w-0">
          <div className="flex items-start gap-2 flex-wrap">
            <h3 className="font-bold text-[hsl(var(--portal-text))] leading-snug">{skool.name_full}</h3>
            {skool.only_4th_rank && (
              <span className="shrink-0 inline-flex items-center gap-1 border border-[hsl(var(--portal-accent-muted))] bg-[hsl(var(--portal-accent-muted))] text-[hsl(var(--portal-accent))] text-[10px] uppercase tracking-widest px-2 py-0.5">
                <AlertTriangle className="w-3 h-3" strokeWidth={1.5} />
                Только <span className="font-mono tabular-nums">4</span> разряд
              </span>
            )}
            {licDanger && (
              <span className="shrink-0 inline-flex items-center gap-1 border border-[hsl(var(--portal-danger))]/40 bg-[hsl(var(--portal-danger))]/15 text-[hsl(var(--portal-danger))] text-[10px] uppercase tracking-widest px-2 py-0.5">
                Лицензия отозвана
              </span>
            )}
          </div>

          <div className="flex flex-wrap gap-x-4 gap-y-1 mt-2 text-sm text-[hsl(var(--portal-text-muted))] font-sans">
            {(skool.city || skool.address) && (
              <span className="inline-flex items-center gap-1">
                <MapPin className="w-3.5 h-3.5 text-[hsl(var(--portal-text-dim))]" strokeWidth={1.5} />
                {[skool.city, skool.address].filter(Boolean).join(", ")}
              </span>
            )}
            {skool.license_to && (
              <span className="inline-flex items-center gap-1">
                <Calendar className="w-3.5 h-3.5 text-[hsl(var(--portal-text-dim))]" strokeWidth={1.5} />
                Лицензия Росгвардии до <span className="font-mono tabular-nums">{skool.license_to}</span>
              </span>
            )}
          </div>

          <div className="flex flex-wrap gap-2 mt-2">
            <span className="inline-flex items-center border border-[hsl(var(--portal-border))] bg-[hsl(var(--portal-surface))] text-[hsl(var(--portal-text-secondary))] text-xs px-2 py-0.5">
              ИНН&nbsp;<span className="font-mono tabular-nums">{skool.inn}</span>
            </span>
            {skool.region && (
              <span className="inline-flex items-center border border-[hsl(var(--portal-border))] bg-[hsl(var(--portal-surface))] text-[hsl(var(--portal-text-secondary))] text-xs px-2 py-0.5">
                <Building2 className="w-3 h-3 mr-1 text-[hsl(var(--portal-text-muted))]" strokeWidth={1.5} />
                {skool.region}
              </span>
            )}
          </div>

          <a
            href={isl}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-1.5 mt-3 border border-[hsl(var(--portal-accent-muted))] bg-[hsl(var(--portal-accent-muted))] text-[hsl(var(--portal-accent))] text-xs px-2.5 py-1.5 hover:border-[hsl(var(--portal-accent))] transition-colors"
          >
            <ExternalLink className="w-3 h-3" strokeWidth={1.5} />
            Лицензия Рособрнадзора:{" "}
            <span className="font-mono tabular-nums">
              {skool.license_number ?? (islStatus === "active" ? "действует" : islStatus || "—")}
            </span>
          </a>

          <button
            type="button"
            onClick={() => setShowReviews((v) => !v)}
            className="inline-flex items-center gap-1.5 mt-3 border border-[hsl(var(--portal-border))] bg-[hsl(var(--portal-surface))] text-[hsl(var(--portal-text-secondary))] text-xs px-2.5 py-1.5 hover:border-[hsl(var(--portal-accent))] hover:text-[hsl(var(--portal-accent))] transition-colors"
          >
            <MessageSquarePlus className="w-3 h-3" strokeWidth={1.5} />
            {showReviews ? "Скрыть отзывы" : "Отзывы"}
          </button>

          <EnrichmentBlock skool={skool} />
        </div>
      </div>

      {showReviews && <SchoolReviews schoolId={skool.id} schoolName={skool.name_full} />}
    </div>
  );
}

function EnrichmentBlock({ skool }: { skool: SchoolCard }) {
  const hasAny =
    !!skool.website ||
    !!skool.ogrn ||
    !!skool.kpp ||
    !!skool.director ||
    skool.employees != null ||
    (skool.social_links?.length ?? 0) > 0 ||
    (skool.founders?.length ?? 0) > 0 ||
    !!skool.registered_at;
  if (!hasAny) return null;
  return (
    <div className="mt-3 grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-1 text-xs text-[hsl(var(--portal-text-muted))] font-sans">
      {skool.website && (
        <span className="truncate">
          <span className="text-[hsl(var(--portal-text-dim))]">Сайт:</span>{" "}
          <a href={skool.website} target="_blank" rel="noopener noreferrer" className="text-[hsl(var(--portal-accent))] hover:underline">
            {skool.website.replace(/^https?:\/\//, "")}
          </a>
        </span>
      )}
      {skool.ogrn && (
        <span><span className="text-[hsl(var(--portal-text-dim))]">ОГРН:</span> <span className="font-mono tabular-nums">{skool.ogrn}</span></span>
      )}
      {skool.kpp && (
        <span><span className="text-[hsl(var(--portal-text-dim))]">КПП:</span> <span className="font-mono tabular-nums">{skool.kpp}</span></span>
      )}
      {skool.registered_at && (
        <span><span className="text-[hsl(var(--portal-text-dim))]">С регистрации:</span> <span className="font-mono tabular-nums">{skool.registered_at}</span></span>
      )}
      {skool.director && (
        <span><span className="text-[hsl(var(--portal-text-dim))]">Руководитель:</span> {skool.director}</span>
      )}
      {skool.employees != null && (
        <span><span className="text-[hsl(var(--portal-text-dim))]">Сотрудников:</span> <span className="font-mono tabular-nums">{skool.employees}</span></span>
      )}
      {(skool.social_links?.length ?? 0) > 0 && (
        <span><span className="text-[hsl(var(--portal-text-dim))]">Соцсети:</span> {skool.social_links!.map((s) => s.value).join(", ")}</span>
      )}
      {(skool.founders?.length ?? 0) > 0 && (
        <span className="sm:col-span-2"><span className="text-[hsl(var(--portal-text-dim))]">Учредители:</span> {skool.founders!.map((f) => f.name).join(", ")}</span>
      )}
    </div>
  );
}
